import { useEffect, useRef, useState } from "react";
import { CaretDown, CaretUp } from "@phosphor-icons/react";

/**
 * Reusable accordion card.
 * - Transparent background (matches page theme), thin border, no gray fill.
 * - Each card manages its own open state, unless `forceOpen` is provided and
 *   changes over time (used by Expand All / Collapse All).
 *
 * Props:
 *  - header: ReactNode rendered inside the header row
 *  - actions: optional ReactNode on the far right of the header (not clickable)
 *  - defaultOpen: boolean (default false)
 *  - forceOpen: optional boolean; when it changes, syncs the card's open state
 *  - testid: base test id — renders `${testid}-toggle` and `${testid}-body`
 *  - children: body content shown when expanded
 */
export default function AccordionCard({ header, actions, defaultOpen = false, forceOpen, testid, anchorId, children }) {
  // Deep links (#anchorId from email buttons) open the card, scroll to it and focus its first text box.
  const isTarget = !!anchorId && window.location.hash === `#${anchorId}`;
  const [open, setOpen] = useState(defaultOpen || isTarget);
  const ref = useRef(null);
  useEffect(() => {
    if (!isTarget) return;
    const t = setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      ref.current?.querySelector("textarea")?.focus({ preventScroll: true });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Sync when the parent broadcasts an expand/collapse all
  useEffect(() => {
    if (typeof forceOpen === "boolean") setOpen(forceOpen);
  }, [forceOpen]);

  return (
    <div className="overflow-hidden hair" ref={ref} id={anchorId}
      data-testid={testid}
      style={{ background: "transparent" }}>
      <div className="w-full flex items-start gap-3 p-4 md:p-5"
        style={{
          background: "transparent",
          borderBottom: open ? "1px solid var(--hair)" : "1px solid transparent",
        }}>
        <button type="button"
          data-testid={testid ? `${testid}-toggle` : undefined}
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          className="flex-1 flex items-start gap-3 text-left min-w-0"
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "inherit" }}>
          <span className="mt-1 shrink-0" style={{ color: "var(--text)" }}>
            {open ? <CaretUp size={18} weight="fill" /> : <CaretDown size={18} weight="fill" />}
          </span>
          <div className="flex-1 min-w-0">{header}</div>
        </button>
        {actions && (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>{actions}</div>
        )}
      </div>
      {open && (
        <div className="p-4 md:p-5" data-testid={testid ? `${testid}-body` : undefined}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Small toggle that flips a parent-owned boolean to broadcast expand/collapse.
 * Usage:
 *   const [allOpen, setAllOpen] = useState(false);
 *   <ExpandAllToggle allOpen={allOpen} onToggle={setAllOpen} />
 *   {items.map(x => <AccordionCard forceOpen={allOpen} ... />)}
 */
export function ExpandAllToggle({ allOpen, onToggle, testid = "expand-all" }) {
  return (
    <button type="button" data-testid={testid}
      onClick={() => onToggle(!allOpen)}
      className="btn-ghost text-xs whitespace-nowrap">
      {allOpen ? (
        <><CaretUp size={12} weight="bold" /> Collapse all</>
      ) : (
        <><CaretDown size={12} weight="bold" /> Expand all</>
      )}
    </button>
  );
}
